import { useEffect, useMemo } from 'react';
import { getCookie } from '@core/js/utils/cookie.util';
import { filter, pick } from 'ramda';
import { trackGamesPersonalizedGrid2 } from './trackingApi';
import { post } from '@core/js/utils/fetch.util';
import { useUserSelector, useAppSelector, useDeviceSelector } from '@core/js/store/hooks';
import { usePortletConfig, usePortletDispatch, usePortletSelector } from '@core/js/components/system/Portlet';
import { dynamicCategoriesFetchedAction } from './actions';
import { log } from 'log';

import {
    parsedMapCategoriesName,
    getCategoriesToFetchFromBI,
    trackLaunchGameFromBI,
    useGetTrackLaunchGameFromBI,
    parseDynamicCategories,
    filterGamesByPGG2
} from '@core/js/components/main/Games/BICategories';

export {
    parsedMapCategoriesName,
    getCategoriesToFetchFromBI,
    trackLaunchGameFromBI,
    useGetTrackLaunchGameFromBI,
    parseDynamicCategories,
    filterGamesByPGG2
};

/* eslint-disable camelcase */
export const fetchCategoriesFromBI = ({
    clientPlatform: client_platform,
    deliveryPlatform: client_delivery_platform,
    pgg2: pgg2Config,
    clientType: client_type,
    guestId: guest_id,
    userDetails,
    categories,
    country: country_code,
    reactPortletId,
    feedName,
    contentType: content_type,
    pasSessionToken: session_id
}) => {
    const { endpointUrl, casino: brand } = pgg2Config;

    if (!endpointUrl || categories.length === 0) {
        return Promise.resolve();
    }

    let request = {
        guest_id,
        brand,
        client_type,
        client_platform,
        client_delivery_platform,
        country_code,
        content_type,
        session_id,
        ...country_code !== 'XX' && { country_code }
    };

    if (userDetails) {
        const { age, sex, username, userId: userid } = userDetails;

        request = { ...request, username, age, userid };
        request = sex ? { ...request, gender: sex.toLowerCase() } : request;
    }

    const affiliate = userDetails ? userDetails.advertiser : getCookie('banner_domainclick');

    request = affiliate ? { ...request, affiliate } : request;

    return Promise.all(categories.map((category) => {
        const { pgg2, nameForBI } = category;
        const { maxGames } = pgg2;

        const requestBody = {
            ...request,
            max_games: maxGames,
            category_names: [nameForBI]
        };

        const trackGamesPersonalizedGrid2Hof = isFallbackPresent =>
            trackGamesPersonalizedGrid2(reactPortletId, feedName, nameForBI, isFallbackPresent);
        const getErrorMsg = msg => `BI category '${nameForBI}' - ${msg}`;

        return new Promise((resolve, reject) => {
            const requestOptions = {
                referrerPolicy: 'no-referrer-when-downgrade',
                referrer: window.location.origin + window.location.pathname
            };
            const timeout = setTimeout(() => reject(getErrorMsg('timeout')), 5000);
            const url = `${endpointUrl}/categories`;

            post({ url, requestBody, requestOptions })
                .then((data) => {
                    if (data?.group === 'control') {
                        return reject(getErrorMsg('in "control" group')); // DEL `
                    }

                    trackGamesPersonalizedGrid2Hof(0);

                    resolve({ nameForBI, pgg2, ...data });
                })
                .catch(err => reject(getErrorMsg(err.message || err.error?.message)))
                .finally(() => clearTimeout(timeout));
        }).catch((e) => {
            trackGamesPersonalizedGrid2Hof(1);
            log('warn', e);
        });
    })).then(filter(Boolean));
};

export const useFetchCategoriesFromBI = () => {
    const { reactPortletId, contentType } = usePortletConfig();
    const { clientPlatform, deliveryPlatform } = useDeviceSelector(pick(['clientPlatform', 'deliveryPlatform']));
    const { pgg2, clientType } = useAppSelector(pick(['pgg2', 'clientType']));
    const { guestId, userDetails, country, pasSessionToken } = useUserSelector(
        pick(['guestId', 'userDetails', 'country', 'pasSessionToken']));
    const {
        feedName,
        feed: { categories: feedCategories = [] } = {}
    } = usePortletSelector(pick(['feedName', 'feed']));
    const categoriesToFetch = useMemo(() => getCategoriesToFetchFromBI(feedCategories), [feedCategories]);
    const dispatch = usePortletDispatch();

    useEffect(
        () => {
            if (categoriesToFetch.length === 0) {
                log('log', 'No BI Categories!');
                dispatch(dynamicCategoriesFetchedAction());
                return;
            }

            (async () => {
                let result;
                try {
                    result = await fetchCategoriesFromBI({
                        categories: categoriesToFetch,
                        feedName,
                        reactPortletId,
                        clientPlatform,
                        deliveryPlatform,
                        pgg2,
                        clientType,
                        guestId,
                        userDetails,
                        country,
                        contentType,
                        pasSessionToken
                    });
                } finally {
                    dispatch(dynamicCategoriesFetchedAction(result));
                }
            })();
        },
        [
            categoriesToFetch,
            feedName,
            reactPortletId,
            clientPlatform,
            deliveryPlatform,
            pgg2,
            clientType,
            contentType,
            guestId,
            userDetails,
            country,
            pasSessionToken,
            dispatch
        ],
        () => feedCategories.length > 0
    );
};
